// src/pages/outsourcing_management/requisitions/RequisitionEditableFieldsForm.tsx
//
// EE-207 Phase 4 (#15) - edit-after-submit limited form. Once a requisition
// has left Draft, only planned delivery date (estimated_delivery_date on the
// wire - the UI label was renamed, the field was not) / supplier / comments remain
// editable (everything else is locked - see EDITABLE_AFTER_SUBMIT_FIELDS in
// RequisitionWorkflowController.py and the matching
// EditableRequisitionFieldsType/EditableFieldsRequisitionIM
// shape). This form's field list is exactly that type - do not add fields
// here without a matching change on the backend input model first.
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { Button, CircularProgress, Grid, TextField, Typography } from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { SupplierAutocomplete } from "components/Dashboard/supplierOption";
import { RequisitionType } from "types/RequisitionTypes";
import { useUpdateRequisitionEditableFields } from "hooks/useRequisitions";
import { canEditDeliveryDate } from "./requisitionWorkflow";
import { inquiryTypeForRequisitionType } from "./requisitionForm";

type PropsType = {
  requisition: RequisitionType;
  onSaved?: (requisition: RequisitionType) => void;
};

const RequisitionEditableFieldsForm: FunctionComponent<PropsType> = ({
  requisition,
  onSaved,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();

  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState(
    requisition.estimated_delivery_date?.slice(0, 10) ?? ""
  );
  const [supplierId, setSupplierId] = useState<number | undefined>(
    requisition.supplier_id ?? undefined
  );
  const [comments, setComments] = useState(requisition.comments ?? "");

  // Re-sync local state whenever the underlying requisition changes (e.g.
  // after a workflow action refetches it, or when reopening for a different
  // requisition), so the form never shows stale values.
  useEffect(() => {
    setEstimatedDeliveryDate(requisition.estimated_delivery_date?.slice(0, 10) ?? "");
    setSupplierId(requisition.supplier_id ?? undefined);
    setComments(requisition.comments ?? "");
  }, [requisition.id, requisition.estimated_delivery_date, requisition.supplier_id, requisition.comments]);


  const isDirty =
    estimatedDeliveryDate !== (requisition.estimated_delivery_date?.slice(0, 10) ?? "") ||
    supplierId !== (requisition.supplier_id ?? undefined) ||
    comments !== (requisition.comments ?? "");

  // update_editable_fields only assigns a field when it arrives non-None, so a
  // JSON body omitting supplierId/estimatedDeliveryDate (which is what
  // `undefined`/"" serialises to) leaves the stored value untouched. Saving a
  // "cleared" field would therefore report success and then silently snap back
  // on refetch. Block it explicitly instead of lying about what was saved.
  const clearedSupplier = requisition.supplier_id != null && supplierId === undefined;
  const clearedDeliveryDate =
    !!requisition.estimated_delivery_date && !estimatedDeliveryDate;
  const hasUnsupportedClear = clearedSupplier || clearedDeliveryDate;
  const CLEAR_UNSUPPORTED_TEXT =
    "Clearing this field isn't supported yet - pick a different value or reset.";

  // Locked at APPROVED - mirrors DELIVERY_DATE_LOCKED_FROM in the workflow
  // controller, which returns a 409 rather than trusting this.
  const deliveryDateLocked = !canEditDeliveryDate(requisition.status);
  const DELIVERY_DATE_LOCKED_TEXT =
    "Fixed once the requisition is approved.";

  const mutation = useUpdateRequisitionEditableFields(token, {
    onSuccess: (data) => {
      addToast({ type: "success", message: "Requisition updated" });
      onSaved?.(data);
    },
    onError: (err) => {
      addToast({
        type: "error",
        message: `Could not update the requisition: ${err}`,
        keep: true,
      });
    },
  });

  const handleSave = () => {
    mutation.mutate({
      id: requisition.id,
      changes: {
        estimatedDeliveryDate: estimatedDeliveryDate || undefined,
        supplierId,
        comments,
      },
    });
  };

  const handleReset = () => {
    setEstimatedDeliveryDate(requisition.estimated_delivery_date?.slice(0, 10) ?? "");
    setSupplierId(requisition.supplier_id ?? undefined);
    setComments(requisition.comments ?? "");
  };

  return (
    <div>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        {deliveryDateLocked
          ? "Edit (Supplier / Comments)"
          : "Edit (Planned Delivery Date / Supplier / Comments)"}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Planned Delivery Date"
            InputLabelProps={{ shrink: true }}
            value={estimatedDeliveryDate}
            onChange={(e) => setEstimatedDeliveryDate(e.target.value)}
            disabled={deliveryDateLocked}
            error={clearedDeliveryDate}
            helperText={
              deliveryDateLocked
                ? DELIVERY_DATE_LOCKED_TEXT
                : clearedDeliveryDate
                ? CLEAR_UNSUPPORTED_TEXT
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SupplierAutocomplete
            value={supplierId}
            onChange={setSupplierId}
            error={clearedSupplier}
            helperText={clearedSupplier ? CLEAR_UNSUPPORTED_TEXT : undefined}
            serviceType={inquiryTypeForRequisitionType(requisition.type_code)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={handleReset} disabled={!isDirty || mutation.isLoading}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isDirty || hasUnsupportedClear || mutation.isLoading}
            sx={{
              background: "var(--mainYellow)",
              "&:hover": { color: "var(--mainYellow)", border: "1px solid var(--mainYellow)", background: "transparent" },
            }}
          >
            {mutation.isLoading ? <CircularProgress size={18} /> : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default RequisitionEditableFieldsForm;
